import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Button, Search as UDSSearch } from '@telus-uds/components-web';
import { ModalFieldLabel, Search as SearchWrapper } from './css';
import { fibreCopperConsolidationField } from '../../../productCatalogueDashboard/wirelineConsolidation/constant';
import { getLocale } from '../../../misc';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import { selectStyles } from './utils';
import { hasAllFilters } from '../fibreInternet/utils';

const Filter = ({
  setFiltersData,
  setSearchByOfferCode,
  setSelectedRows,
  selectedCouponData,
  setData,
  isLoading,
  isTSBModal,
  isVulcanFlow,
  isFibreModal,
  isCopperModal,
  isInternetBackupSelected,
  activeTab,
  isFibreEastRestricted
}) => {
  const locale = getLocale();
  const {
    vulcanWebform: {
      filterByTerm,
      filterBySpeed,
      bundledOffer,
      searchByOfferCode,
      selectTheTerm,
      selectTheSpeed,
      selectTheBundled,
      buttonApply,
      buttonReset,
      offerTermMonthToMonth,
      vulThirtyMonth,
      vulSixtyMonth,
      yes,
      no,
      speedLabels
    }
  } = locale === 'en-US' ? EN : FR;

  const termOptions = [
    { value: 'Month to Month', label: offerTermMonthToMonth },
    { value: '36 Months', label: vulThirtyMonth },
    { value: '60 Months', label: vulSixtyMonth }
  ];

  // FR-008: Fibre East (non-ILEC) orders allow MTM and 36 Months only — 60 Months is excluded.
  // FR-003a: Vulcan flow orders share the same MTM + 36 Months only restriction (60 Months excluded).
  const fibreEastTermOptions =
    isFibreEastRestricted || isVulcanFlow
      ? termOptions.filter(o => o.value !== '60 Months')
      : termOptions;

  const bundledOptions = [
    { value: 'Yes', label: yes },
    { value: 'No', label: no }
  ];

  // Get speed options and filter for Vulcan flow and product type
  const getSpeedOptions = () => {
    // Look up by name (robust against field reordering in constant.js)
    const allSpeedOptions =
      (fibreCopperConsolidationField.find(f => f.name === 'speed') || {})
        .options || [];

    // Filter by product type first
    let filteredByProduct = allSpeedOptions;
    if (isFibreModal) {
      filteredByProduct = allSpeedOptions.filter(option =>
        option.value.includes('Fibre Internet')
      );
    } else if (isCopperModal) {
      filteredByProduct = allSpeedOptions.filter(option =>
        option.value.includes('Office Internet')
      );
    }

    // Then filter by flow type (Vulcan vs WLN)
    let result;
    if (isVulcanFlow) {
      // FR-004/FR-005/FR-006: For Vulcan flow, show only the 1.5G Fibre speed —
      // 3G is no longer sellable and must not appear as a selectable option.
      result = filteredByProduct.filter(
        option => option.value === 'Fibre Internet 1.5G: 1.5G/940Mbps'
      );
    } else if (isFibreEastRestricted) {
      // For Fibre East (non-ILEC) orders in Ontario/Québec with PureFibre East = No,
      // show only 1.5G and 3G Fibre speeds — same restriction as Vulcan flow
      result = filteredByProduct.filter(
        option =>
          option.value === 'Fibre Internet 1.5G: 1.5G/940Mbps' ||
          option.value === 'Fibre Internet 3G: 3000 Mbps'
      );
    } else {
      // WLN flow: show all speeds for the selected product, but 2.5G is only
      // available when "TELUS Internet Backup" is checked on the product
      // checklist (TIB is a WLN-only product).
      result = isInternetBackupSelected
        ? filteredByProduct
        : filteredByProduct.filter(
            option => option.value !== 'Fibre Internet 2.5G: 2500 Mbps'
          );
    }

    // FR-038: remap labels to locale-aware French labels where available
    if (speedLabels && Object.keys(speedLabels).length > 0) {
      return result.map(option => ({
        ...option,
        label: speedLabels[option.value] || option.label
      }));
    }
    return result;
  };

  const [searchString, setSearchString] = useState('');
  const [filters, setFilters] = useState({
    term: [],
    speed: [],
    bundled: []
  });
  // T012 [US2]: Lock Term to '36 Months' and Bundled to 'Yes' only for TSB in a Vulcan flow on the Other Offers tab
  const isOtherOffersLocked =
    isVulcanFlow && isTSBModal && activeTab === 'Other Offers';

  const isDisabled =
    !filters.term.length ||
    (!isTSBModal && !filters.speed.length) ||
    !filters.bundled.length;

  // Predefined filters for Vulcan flow fibre: 36 Months term, Yes for bundled
  const vulcanFibreDefaultFilters = {
    term: ['36 Months'],
    speed: [],
    bundled: ['Yes']
  };

  useEffect(() => {
    if (hasAllFilters(selectedCouponData)) {
      setFilters({
        term: selectedCouponData.term || [],
        speed: selectedCouponData.speed || [],
        bundled: selectedCouponData.bundled || []
      });
      setFiltersData({
        term: selectedCouponData.term || [],
        speed: selectedCouponData.speed || [],
        bundled: selectedCouponData.bundled || []
      });
    } else if (isVulcanFlow && isFibreModal) {
      setFilters(vulcanFibreDefaultFilters);
      setFiltersData(vulcanFibreDefaultFilters);
    }
  }, []);

  // T012 [US2]: Pre-fill and lock Term/Bundled when Other Offers tab is active in Vulcan flow
  useEffect(() => {
    if (isOtherOffersLocked) {
      setFilters(vulcanFibreDefaultFilters);
      setFiltersData(vulcanFibreDefaultFilters);
    }
  }, [isOtherOffersLocked]);

  // 2️⃣  Generic handler to update the correct key
  const handleChange = (key, selected) => {
    setData({ orders: [], count: 0 });
    setSelectedRows([]);
    // single option → store as array
    const valuesArray = selected ? [selected.value] : [];
    setFilters(prev => ({
      ...prev,
      [key]: valuesArray
    }));
  };

  const onSearchClick = () => {
    setSearchByOfferCode(searchString);
  };

  const onClear = () => {
    setSearchByOfferCode('');
    setSearchString('');
  };

  const applyFilters = () => {
    setFiltersData(filters);
  };

  const resetFilters = () => {
    // Term/Bundled are locked (disabled) for the Vulcan Fibre flow and the
    // Other-Offers-locked TSB case — Reset must not clear those preselected,
    // non-editable values, only the user-editable Speed filter.
    const isLocked = (isVulcanFlow && isFibreModal) || isOtherOffersLocked;
    setFilters(isLocked ? vulcanFibreDefaultFilters : { term: [], speed: [], bundled: [] });
    setFiltersData({});
    setSelectedRows([]);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '2rem',
          marginLeft: 60
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ModalFieldLabel>{filterByTerm}</ModalFieldLabel>
          <Select
            id="term"
            name="term"
            placeholder={selectTheTerm}
            options={fibreEastTermOptions}
            styles={selectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            value={fibreEastTermOptions.filter(o =>
              filters.term.includes(o.value)
            )}
            onChange={selected => handleChange('term', selected)}
            isDisabled={isOtherOffersLocked}
          />
        </div>

        {!isTSBModal && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <ModalFieldLabel>{filterBySpeed}</ModalFieldLabel>
            <Select
              id="speed"
              name="speed"
              placeholder={selectTheSpeed}
              options={getSpeedOptions()}
              styles={selectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              value={getSpeedOptions().filter(o =>
                filters.speed.includes(o.value)
              )}
              onChange={selected => handleChange('speed', selected)}
            />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ModalFieldLabel>{bundledOffer}</ModalFieldLabel>
          <Select
            id="bundled"
            name="bundled"
            placeholder={selectTheBundled}
            options={bundledOptions}
            styles={selectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            value={bundledOptions.filter(o =>
              filters.bundled.includes(o.value)
            )}
            onChange={selected => handleChange('bundled', selected)}
            isDisabled={(isVulcanFlow && isFibreModal) || isOtherOffersLocked}
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '18px',
          justifyContent: 'center',
          margin: '30px 0'
        }}
      >
        <Button
          type="button"
          variant={{ priority: 'high' }}
          onClick={applyFilters}
          disabled={isDisabled || isLoading}
          style={{
            flex: 1,
            maxWidth: '200px',
            minWidth: '120px'
          }}
        >
          {buttonApply}
        </Button>
        <Button
          type="button"
          variant={{ priority: 'low' }}
          onClick={resetFilters}
          disabled={isDisabled || isLoading}
          style={{
            flex: 1,
            maxWidth: '200px',
            minWidth: '120px'
          }}
        >
          {buttonReset}
        </Button>
      </div>
      <br />
      <SearchWrapper>
        <div style={{ marginLeft: 20 }}>
          <UDSSearch
            placeholder={searchByOfferCode}
            value={searchString || ''}
            onClear={() => onClear()}
            onChange={setSearchString}
            onSubmit={() => onSearchClick()}
          />
        </div>
      </SearchWrapper>
    </>
  );
};

Filter.propTypes = {
  setFiltersData: PropTypes.func.isRequired,
  setSearchByOfferCode: PropTypes.func.isRequired,
  setSelectedRows: PropTypes.func.isRequired,
  selectedCouponData: PropTypes.shape({
    term: PropTypes.arrayOf(PropTypes.string),
    speed: PropTypes.arrayOf(PropTypes.string),
    bundled: PropTypes.arrayOf(PropTypes.string),
    data: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  setData: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isTSBModal: PropTypes.bool,
  isVulcanFlow: PropTypes.bool,
  isFibreModal: PropTypes.bool,
  isCopperModal: PropTypes.bool,
  isInternetBackupSelected: PropTypes.bool,
  activeTab: PropTypes.string,
  isFibreEastRestricted: PropTypes.bool
};

export default Filter;
